/**
 * Component: sa-why-product-list
 * Handles:
 *   1. Color swatch click → variant switch (image + price + select + buttons)
 *      Uses the same data-title matching algorithm as TTProductItem.changeOption
 *   2. Mobile carousel navigation (prev / next / scroll progress)
 *   3. Buy-now href sync on variant change
 */
if (!customElements.get('sa-why-product-list')) {

  class SaWhyProductList extends HTMLElement {
    constructor() {
      super();
      this.handleSwatchClick = this.handleSwatchClick.bind(this);
      this.handlePrev = this.handlePrev.bind(this);
      this.handleNext = this.handleNext.bind(this);
      this.handleScroll = this.handleScroll.bind(this);
      this._onResize = this._onResize.bind(this);
    }

    connectedCallback() {
      this.track = this.querySelector('.sa-why-product-list__track');
      this.prevBtn = this.querySelector('.sa-why-product-list__ctrl-btn--prev');
      this.nextBtn = this.querySelector('.sa-why-product-list__ctrl-btn--next');
      this.progressBar = this.querySelector('.sa-why-product-list__progress-bar');

      this.addEventListener('click', this.handleSwatchClick);

      if (this.prevBtn) this.prevBtn.addEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.addEventListener('click', this.handleNext);
      if (this.track) this.track.addEventListener('scroll', this.handleScroll, { passive: true });

      requestAnimationFrame(() => this.updateProgress());

      if (this.track && typeof ResizeObserver !== 'undefined') {
        this._ro = new ResizeObserver(this._onResize);
        this._ro.observe(this.track);
      }
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.handleSwatchClick);
      if (this.prevBtn) this.prevBtn.removeEventListener('click', this.handlePrev);
      if (this.nextBtn) this.nextBtn.removeEventListener('click', this.handleNext);
      if (this.track) this.track.removeEventListener('scroll', this.handleScroll);
      if (this._ro) {
        this._ro.disconnect();
        this._ro = null;
      }
    }

    _onResize() {
      this.updateProgress();
    }

    // ── Variant switching ───────────────────────────────────────────────────

    handleSwatchClick(e) {
      const swatch = e.target.closest('.sa-why-product-list__swatches .option-item');
      if (!swatch) return;

      const card = swatch.closest('.sa-why-product-list__card');
      if (!card) return;

      const value = swatch.dataset.value || '';
      // Position is stored on the parent <ul data-position="N"> (1-indexed)
      const position = parseInt(swatch.parentElement.dataset.position || '', 10);
      if (!value || !position) return;

      // Find currently active variant image
      const activeImg = card.querySelector('.product-img.active');
      if (!activeImg) return;

      // Replicate TTProductItem.changeOption title-matching algorithm:
      // variant.title is "Color" (single option) or "Color / Size / ..." (multi)
      const parts = (activeImg.dataset.title || '').split(' / ');
      parts[position - 1] = value;
      const newTitle = parts.join(' / ');

      const allImgs = card.querySelectorAll('.product-img');
      const newImg = Array.from(allImgs).find(el => el.dataset.title === newTitle);
      if (!newImg) return;

      const variantId = newImg.dataset.id || '';
      const available = newImg.dataset.available === 'true';
      const price = newImg.dataset.price || '';
      const comparePrice = newImg.dataset.compare_price || '';

      // Switch active image
      activeImg.classList.remove('active');
      newImg.classList.add('active');

      // Switch active swatch
      const prevActiveSwatch = swatch.parentElement.querySelector('.option-item.active');
      if (prevActiveSwatch) prevActiveSwatch.classList.remove('active');
      swatch.classList.add('active');

      // Update price display
      const priceEl = card.querySelector('.price--highlight');
      if (priceEl) priceEl.textContent = price;

      const comparePriceEl = card.querySelector('.price--compare');
      if (comparePriceEl) comparePriceEl.textContent = comparePrice;

      // Sync hidden variant select (product-form component listens for this)
      const select = card.querySelector('select.variant-select');
      if (select) {
        select.value = variantId;
        select.dispatchEvent(new Event('change', { bubbles: true }));
      }

      // Sync selling_plan input if present
      const sellingPlanInput = card.querySelector('[name="selling_plan"]');
      if (sellingPlanInput) sellingPlanInput.value = newImg.dataset.selling_plan || '';

      // Update add-to-cart button state
      const cartBtn = card.querySelector('.sa-why-product-list__btn--cart');
      if (cartBtn) {
        cartBtn.disabled = !available;
        const loaderText = cartBtn.querySelector('.loader-button__text');
        const addText = cartBtn.dataset.btnAdd_to_cart || '';
        const soldText = cartBtn.dataset.btnSold_out || '';
        if (loaderText && addText) {
          loaderText.textContent = available ? addText : soldText;
        } else if (addText) {
          cartBtn.textContent = available ? addText : soldText;
        }
      }

      // Update product-form availability
      const productForm = card.querySelector('product-form');
      if (productForm) productForm.dataset.available = String(available);

      // Update buy-now link
      const buyBtn = card.querySelector('.sa-why-product-list__btn--buy');
      if (buyBtn) {
        const cartBase = buyBtn.dataset.cartBase;
        if (available && cartBase && variantId) {
          buyBtn.href = `${cartBase}/${variantId}:1`;
          buyBtn.classList.remove('sa-why-product-list__btn--disabled');
        } else {
          buyBtn.href = '#';
          buyBtn.classList.add('sa-why-product-list__btn--disabled');
        }
      }
    }

    // ── Mobile carousel ────────────────────────────────────────────────────

    getCardWidth() {
      if (!this.track) return 221;
      const card = this.track.querySelector('.sa-why-product-list__card');
      if (!card) return 221;
      const gap = parseFloat(getComputedStyle(this.track).gap) || 10;
      return card.offsetWidth + gap;
    }

    handlePrev() {
      if (!this.track) return;
      this.track.scrollBy({ left: -this.getCardWidth(), behavior: 'smooth' });
    }

    handleNext() {
      if (!this.track) return;
      this.track.scrollBy({ left: this.getCardWidth(), behavior: 'smooth' });
    }

    handleScroll() {
      this.updateProgress();
    }

    updateProgress() {
      if (!this.track || !this.progressBar) return;
      const { scrollLeft, scrollWidth, clientWidth } = this.track;
      const maxScroll = scrollWidth - clientWidth;
      if (maxScroll <= 0) {
        this.progressBar.style.width = '100%';
        if (this.prevBtn) this.prevBtn.disabled = true;
        if (this.nextBtn) this.nextBtn.disabled = true;
        return;
      }
      const pct = (scrollLeft / maxScroll) * 100;
      this.progressBar.style.width = Math.min(100, Math.max(0, pct)) + '%';
      if (this.prevBtn) this.prevBtn.disabled = scrollLeft <= 1;
      if (this.nextBtn) this.nextBtn.disabled = scrollLeft >= maxScroll - 1;
    }
  }

  customElements.define('sa-why-product-list', SaWhyProductList);
}
